import { useEffect, useState, type CSSProperties, type JSX } from 'react';
import { InputNumber, Select, Tag } from 'antd';
import { ProfileOutlined } from '@ant-design/icons';
import type { PosMoneyResult, ShippingFeePayer, ShippingMode } from '@tcr-erp/shared';
import { formatNumber } from '@/utils/format';
import { moneyFormatter, moneyParser, moneyInputKeyDownGuard } from '../utils/moneyInput';
import { ServiceFeeRow, type ServiceOption, type ServiceFeeValue } from './ServiceFeeRow';

export interface OrderInfoPanelProps {
  itemCount: number;
  /** Căn tiêu đề của panel; màn chi tiết đơn đặt sang phải trong card tài chính. */
  titleAlign?: 'left' | 'right';
  /** Mọi con số của khối tiền — pipeline `computePosMoney` đã tính sẵn. Panel KHÔNG tự suy ra từ items. */
  money: PosMoneyResult;
  /** Bỏ trống ⇒ đơn bán tại quầy: ẩn người trả phí, phí VC, trả trước. */
  shippingMode?: ShippingMode;
  feePayer?: ShippingFeePayer;
  onFeePayerChange?: (v: ShippingFeePayer) => void;
  /**
   * TCR-1298 — "freeship đang áp dụng" (KM tự động HOẶC voucher nhập tay loại
   * free_shipping), truyền là boolean chứ KHÔNG phải số tiền:
   *
   * - KHÔNG dùng `money.shippingDiscount`: giá trị đó LUÔN bị kẹp về 0 khi
   *   `feePayer === 'seller'` (không còn phần "buyer trả" để mà giảm), nên
   *   nếu dùng nó làm điều kiện khoá dropdown thì ngay sau khi tự chuyển sang
   *   "Người bán" nó sẽ tụt về 0 và tự mở khoá lại — vòng lặp vô nghĩa.
   * - KHÔNG dùng số tiền freeship THÔ từ `/pos/promotions/evaluate`
   *   (`shipping_discount`) làm nguồn DUY NHẤT: voucher freeship NHẬP TAY
   *   chưa có số tiền giảm thật (BE luôn trả `discount_amount: 0` cho loại
   *   này lúc validate — `VoucherApplyService.calculateDiscount()`), nên
   *   không có "số dương" nào để so sánh > 0. Bên gọi (`POS/index.tsx`,
   *   `Orders/edit.tsx`) phải tự OR hai nguồn: `shipping_discount > 0` (KM tự
   *   động) HOẶC có voucher `voucherType === 'free_shipping'` đang áp
   *   (`hasFreeshipVoucher`).
   */
  freeshipActive?: boolean;
  prepaidAmount?: number;
  onPrepaidChange?: (v: number) => void;
  /** Dòng phụ thu — cần khi KHÔNG readOnly. Thiếu ở readOnly thì panel tự đọc `money.serviceLines`. */
  surchargeRow?: { value: ServiceFeeValue; onChange: (v: ServiceFeeValue) => void };
  /** Dòng phí VC, CHỈ chế độ tự vận chuyển (`shippingMode === 'self'`) và không readOnly. */
  shippingRow?: { value: ServiceFeeValue; onChange: (v: ServiceFeeValue) => void };
  serviceOptions?: { surcharge: ServiceOption[]; shipping: ServiceOption[] };
  /**
   * `serviceOptions` ở trên đang tải LẦN ĐẦU (chưa có cache) — truyền thẳng
   * xuống cả 2 `ServiceFeeRow` để hiện skeleton thay vì Select có thể hiện
   * trống gây hiểu nhầm mất lựa chọn (TCR-1104). Optional: bỏ trống ⇒ hành vi
   * y hệt trước đây (dùng ở `Orders/detail.tsx`/`edit.tsx` qua `readOnly`).
   */
  serviceOptionsLoading?: boolean;
  defaultVatRates?: { surcharge: number; shipping: number };
  /** ĐVVC: phí hãng báo, read-only. */
  carrierFee?: number;
  /**
   * Trạng thái ước tính phí của hãng đang chọn. `fee: 0` với `status: 'ok'` là
   * tuyến free-ship hợp lệ (KM), không phải lỗi — chỉ `status !== 'ok'` mới cảnh báo.
   */
  selectedCarrierFeeStatus?: 'loading' | 'ok' | 'error' | 'timeout';
  /**
   * TCR-1084/1086 — màn POS bật cờ này: đơn đi ĐVVC thì KHÔNG hiển thị dòng
   * "Phí vận chuyển" trong cụm tiền, vì phí đó không cộng cũng không trừ vào
   * doanh thu (shop chỉ thu hộ hãng). Trên POS phí hãng vẫn xem được ở danh
   * sách đối tác vận chuyển.
   *
   * Chi tiết đơn và sửa đơn KHÔNG bật: ở hai màn đó đây là chỗ duy nhất hiện
   * phí ĐVVC, cần cho việc đối soát với hãng.
   *
   * Cờ riêng chứ KHÔNG dùng lại `readOnly`: "chỉ đọc" và "màn POS" là hai khái
   * niệm khác nhau, buộc chúng vào nhau là chỗ để lỗi trôi vào lần sửa sau.
   */
  hideCarrierFeeRow?: boolean;
  /** Màn chi tiết đơn / sửa đơn: mọi dòng render text tĩnh, không Select/InputNumber. */
  readOnly?: boolean;
  /** Chỉ để HIỂN THỊ — `money.orderDiscount`/`customerPayable` đã trừ khoản này rồi. */
  voucherDiscount?: number;
  /** Khuyến mãi tự động (TCR-1069). Chỉ để HIỂN THỊ — đã nằm trong `money.orderDiscount`. */
  promotionDiscount?: number;
  /** Chỉ để HIỂN THỊ (đơn B2B). */
  tradeDiscount?: number;
  /** Chỉ để HIỂN THỊ. */
  loyaltyRedeemValue?: number;
  /**
   * TCR-1044 — MÃ voucher / TÊN chương trình khuyến mãi đã áp vào đơn, để thu
   * ngân và kế toán đối chiếu được đơn này được giảm theo chương trình nào.
   * Chỉ số tiền thôi thì không đối chiếu được, đó chính là yêu cầu của ticket.
   *
   * Rỗng ⇒ không render gì thêm: màn POS (đơn đang dựng) không có danh sách
   * này và giữ nguyên giao diện cũ.
   */
  appliedLabels?: {
    /** Mã voucher thật đã chốt, ví dụ `GIAM20%T8`. */
    vouchers?: Array<{ code: string; amount: number }>;
    /** Tên chương trình khuyến mãi tự động (Promotion Engine) giảm TIỀN HÀNG. */
    promotions?: Array<{ name: string; amount: number }>;
    /**
     * Chương trình giảm PHÍ VẬN CHUYỂN (freeship). Tách khỏi `promotions` vì
     * chip xếp ngay dưới dòng tổng mà nó cộng vào: để lẫn thì màn hiện "Tổng
     * chiết khấu: -0" ngay trên một chip "-30.000" (xem `splitPromotionLabels`).
     */
    shippingPromotions?: Array<{ name: string; amount: number }>;
  };
}

const FEE_PAYER_OPTIONS: { value: ShippingFeePayer; label: string }[] = [
  { value: 'seller', label: 'Người bán' },
  { value: 'buyer', label: 'Người mua' },
];

/**
 * TCR-2605 — tooltip giải thích vì sao con số phí vận chuyển ở POS là ƯỚC
 * TÍNH: phí hãng báo giá lúc tạo vận đơn, hãng báo phí thật sau nên số sẽ
 * được cập nhật.
 */
const CARRIER_FEE_ESTIMATE_HINT =
  'Phí hãng báo giá lúc tạo vận đơn, sẽ được cập nhật khi hãng báo phí thật.';

// QC round 5 (TCR-1104): 78% = 52% × 1.5 — do bảng dùng `tableLayout: 'fixed'`
// (xem comment ở khai báo <table>), cột 1 lấy width từ DÒNG ĐẦU TIÊN của bảng
// (luôn dùng LABEL) rồi áp cho MỌI dòng khác — width khai riêng trên từng
// `<td>` của dòng sau (kể cả trong `ServiceFeeRow`) bị trình duyệt bỏ qua,
// đã kiểm chứng thực tế bằng Playwright trước khi đổi ở đây.
//
// Hạ xuống 74% khi tăng font-size dropdown "Người trả phí vận chuyển" (10px
// ⇒ 13px, xem `.pos-fee-payer-select .ant-select-selection-item` trong
// POSPage.css): đo thực tế bằng Playwright cho thấy "Người bán"/"Người mua"
// cần 83px nhưng cột AMOUNT ở 78% chỉ còn 73px sau padding — text bị
// `.ant-select-selection-item` tự ellipsis thành "Người...". 74% cho AMOUNT
// ~90px, đủ dư so với 83px cần. Các label dài hơn ở dòng khác (vd "Dịch vụ
// phí vận chuyển (VAT X%)") ellipsis/wrap được, không đẩy layout — không vi
// phạm mục đích gốc của TCR-1104 (chặn TRÀN ngang, không cấm ellipsis).
const LABEL: CSSProperties = { color: '#555', paddingBottom: 6, width: '74%' };
const AMOUNT: CSSProperties = { textAlign: 'right', fontWeight: 600, paddingBottom: 6 };
const BOLD_LABEL: CSSProperties = { color: '#333', fontWeight: 700, paddingBottom: 6 };
const BOLD_AMOUNT: CSSProperties = { textAlign: 'right', fontWeight: 700, paddingBottom: 6 };

function money(n: number): string {
  return formatNumber(n);
}

/**
 * Khối "Thông tin đơn hàng" ở cột phải màn POS: các dòng tiền của đơn, cộng ba ô
 * nhập chỉ đơn giao hàng mới có (người trả phí, phí vận chuyển, khách trả trước).
 *
 * KHÔNG tự tính gì — nhận thẳng `money: PosMoneyResult` từ `computePosMoney`
 * (cùng hàm BE dùng). Trước Task 18, panel này và `ShippingTotals` mỗi cái tự
 * tính một phần công thức riêng và đã từng trôi lệch số hiển thị với số lưu vào
 * đơn (`orderRevenue` bị hiểu nhầm thành "tổng tiền có thuế"). Giờ cả hai chỉ
 * còn hiển thị — một nguồn số duy nhất.
 */
export function OrderInfoPanel({
  itemCount,
  titleAlign = 'left',
  money: m,
  shippingMode,
  feePayer = 'seller',
  onFeePayerChange,
  freeshipActive: freeshipActiveProp = false,
  prepaidAmount = 0,
  onPrepaidChange,
  surchargeRow,
  shippingRow,
  serviceOptions,
  serviceOptionsLoading,
  defaultVatRates,
  carrierFee = 0,
  selectedCarrierFeeStatus,
  hideCarrierFeeRow = false,
  readOnly = false,
  voucherDiscount = 0,
  promotionDiscount = 0,
  tradeDiscount = 0,
  loyaltyRedeemValue = 0,
  appliedLabels,
}: OrderInfoPanelProps): JSX.Element {
  const [showTaxDetail, setShowTaxDetail] = useState(false);
  const hasShipping = !!shippingMode;
  // Người trả phí / Khách trả trước: sửa được khi panel KHÔNG readOnly —
  // CÙNG một gate với Phụ thu/Phí vận chuyển tự vận chuyển bên dưới. Trước
  // đây `Orders/edit.tsx` cần một cờ riêng (`editableShippingFee`) để mở khoá
  // RIÊNG hai dòng này ở chế độ ĐVVC trong khi vẫn hard-code `readOnly` cho
  // cả panel — nay nơi gọi truyền thẳng `readOnly={!isFullyEditable}` (động
  // theo trạng thái đơn) nên `!readOnly` đã đúng nghĩa cho MỌI dòng, không
  // cần cờ phụ nữa.
  const feeFieldsEditable = !readOnly;
  // Freeship áp dụng ⇒ phí luôn tính cho Người bán, không cho thu ngân chọn
  // Người mua nữa (nghiệp vụ: shop đã cam kết miễn phí ship cho khách).
  // Đọc `freeshipActiveProp` (boolean từ bên gọi, xem doc ở prop) chứ không
  // phải `m.shippingDiscount` — lý do xem comment tại khai báo prop.
  const freeshipActive = feeFieldsEditable && freeshipActiveProp;
  useEffect(() => {
    if (freeshipActive && feePayer !== 'seller') {
      onFeePayerChange?.('seller');
    }
  }, [freeshipActive, feePayer, onFeePayerChange]);
  // Đơn ĐVVC trên POS: giấu cả dòng phí lẫn cảnh báo "chưa tính được phí" —
  // cảnh báo về một con số không hiển thị thì chỉ làm thu ngân rối.
  const showShippingRow = hasShipping && !(hideCarrierFeeRow && shippingMode === 'carrier');
  // Chỉ cảnh báo khi CÓ trạng thái ước tính thật và trạng thái đó nói là hỏng.
  // `undefined` = bên gọi không hề chạy ước tính (đơn đã lưu ở màn chi tiết /
  // modal tóm tắt / sửa đơn khi đơn đã khoá) — ở đó phí là việc đã rồi, không
  // phải một phép ước tính đang chờ, nên không có gì để cảnh báo. So thẳng
  // `!== 'ok'` sẽ biến chính sự VẮNG MẶT của thông tin thành lời báo động, dán
  // "Chưa tính được phí" lên mọi đơn ĐVVC đã chốt ngay dưới dòng phí đang hiện
  // đúng số của nó.
  const carrierFeeUnavailable =
    selectedCarrierFeeStatus !== undefined && selectedCarrierFeeStatus !== 'ok';
  // TCR-2605 — POS + đơn ĐVVC: dòng "Phí vận chuyển" bị ẩn (TCR-1084/1086 —
  // phí hãng không cộng/trừ vào doanh thu), nên lúc lên đơn thu ngân KHÔNG
  // thấy được con số phí hãng. Dòng "Phí vận chuyển ước tính" hiện con số ƯỚC
  // TÍNH kèm ai chịu khoản đó — CHỈ để hiển thị, KHÔNG đụng tổng tiền (mọi
  // số hạch toán giữ nguyên, `money` không bị đụng).
  //
  // Chỉ hiện khi bên gọi thật sự có ước tính: `status === 'ok'` VÀ phí > 0
  // (freeship `fee: 0` là tuyến hợp lệ, không phải lỗi), hoặc `status ===
  // 'loading'` (đang chờ hãng báo — hiện "Đang tính…"). `error`/`timeout`
  // thì không có con số nào để hiện; chỗ nói về việc đó là cảnh báo "Chưa tính
  // được phí".
  const showCarrierFeeEstimate =
    hasShipping &&
    shippingMode === 'carrier' &&
    hideCarrierFeeRow &&
    (selectedCarrierFeeStatus === 'loading' ||
      (selectedCarrierFeeStatus === 'ok' && carrierFee > 0));
  const surchargeLine = m.serviceLines.find((l) => l.kind === 'surcharge');
  const shippingLine = m.serviceLines.find((l) => l.kind === 'shipping');
  const hasTaxDetail = m.taxBreakdowns.length > 0;

  return (
    <div className="pos-order-info-panel">
      {/* Tiêu đề hiện ở CẢ hai bối cảnh. Ở POS nó thay cho thanh
          "THÔNG TIN ĐƠN HÀNG" trên đỉnh cột phải (`CustomerSidebar`, nay ẩn đi
          bằng `hideHeader`): đặt sát ngay trên cụm tiền thì nhãn mới dính vào
          đúng khối nó gọi tên, lại kèm được số sản phẩm. */}
      <div
        data-testid="order-info-panel-title"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: titleAlign === 'right' ? 'flex-end' : 'flex-start',
          gap: 6,
          fontSize: 13,
          fontWeight: 700,
          color: '#333',
          marginBottom: 8,
        }}
      >
        <ProfileOutlined />
        <span>
          Thông tin đơn hàng{' '}
          <span style={{ color: '#a4193d' }}>({itemCount} sản phẩm)</span>
        </span>
      </div>

      {/* TCR-1104: `tableLayout: 'fixed'` bắt buộc — dòng Phụ thu/Phí vận
          chuyển giờ hiện "VAT X% | Tên dịch vụ" (dài hơn tên thuần trước đây).
          Ở `table-layout: auto` (mặc định), cột tự giãn theo nội dung DÀI
          NHẤT bất kể `width` khai trên `<td>`, đẩy cả bảng rộng hơn khung
          panel bên phải — cột tiền bị đẩy tràn ra ngoài viewport, mấy dòng
          "Tổng tiền hàng"/"Tổng chiết khấu" thậm chí mất trắng giá trị. Fixed
          layout buộc trình duyệt tôn trọng `width: '52%'` của `<td>` nhãn,
          text dài tự ellipsis (`.ant-select-selection-item` đã có sẵn
          overflow/text-overflow/white-space phù hợp), không đẩy layout. */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, tableLayout: 'fixed' }}>
        <tbody>
          {hasShipping && (
            <tr>
              <td style={LABEL}>Người trả phí vận chuyển:</td>
              {!feeFieldsEditable ? (
                <td style={AMOUNT}>
                  {FEE_PAYER_OPTIONS.find((o) => o.value === feePayer)?.label ?? feePayer}
                </td>
              ) : (
                <td style={AMOUNT}>
                  <Select<ShippingFeePayer>
                    aria-label="Người trả phí vận chuyển"
                    className="pos-fee-payer-select"
                    variant="borderless"
                    size="small"
                    /* `minWidth: 110` từng vượt bề rộng thật của cột AMOUNT
                       (22% bảng, `tableLayout: 'fixed'`) trong `.pos-order-
                       info-stack` — vì đây là `width: 'auto'` nên KHÔNG co
                       theo cell, tràn ra ngoài panel phải ~9px. `overflow-y:
                       auto` của `.pos-order-info-stack` (không khai
                       `overflow-x`) tự suy thành `overflow-x: auto` theo CSS
                       Overflow spec, biến khoảng tràn đó thành thanh cuộn
                       ngang cho CẢ khối. `width: '100%'` để Select LUÔN khớp
                       đúng bề rộng ô `<td>` chứa nó, không phụ thuộc độ dài
                       nhãn "Người bán"/"Người mua". Font-size/weight của
                       `.ant-select-selection-item` phải hạ qua CSS class
                       (`.pos-fee-payer-select` trong POSPage.css) — style
                       prop ở đây chỉ áp cho wrapper, KHÔNG lọt xuống được ô
                       con vì antd tự set font-size trên chính nó. */
                    style={{ width: '100%' }}
                    value={feePayer}
                    onChange={(v) => onFeePayerChange?.(v)}
                    options={FEE_PAYER_OPTIONS}
                    disabled={freeshipActive}
                    /* Popup mặc định khớp bề rộng ô đóng (rất hẹp, xem comment
                       trên) — khiến "Người bán"/"Người mua" bị cắt/xuống dòng
                       ngay trong danh sách thả xuống. Cho popup tự co theo nội
                       dung, không phụ thuộc ô đóng — pattern đã dùng chung
                       toàn repo (`popupMatchSelectWidth={false}`). */
                    popupMatchSelectWidth={false}
                  />
                </td>
              )}
            </tr>
          )}

          <tr data-testid="money-row-goods" data-label="Tổng tiền hàng">
            <td style={LABEL}>Tổng tiền hàng:</td>
            <td style={AMOUNT}>{money(m.totalGoods)}</td>
          </tr>
          <tr data-testid="money-row-discount" data-label="Tổng chiết khấu">
            <td style={LABEL}>Tổng chiết khấu:</td>
            <td style={AMOUNT}>-{money(m.totalLineDiscount)}</td>
          </tr>

          {voucherDiscount > 0 && (
            <tr data-testid="money-row-voucher">
              <td style={LABEL}>Giảm giá voucher:</td>
              <td style={{ ...AMOUNT, color: '#a4193d' }}>-{money(voucherDiscount)}</td>
            </tr>
          )}
          {/* TCR-1044: mã voucher THẬT đã chốt — một dòng riêng cho mỗi mã, để
              đối chiếu đơn với chương trình.

              Số tiền chỉ lặp lại khi đơn có TỪ HAI mã trở lên (lúc đó mới cần
              biết mã nào giảm bao nhiêu). Một mã mà in số hai dòng liền nhau
              thì đọc như đang bị trừ hai lần. */}
          {(appliedLabels?.vouchers ?? []).map((voucher) => (
            <tr key={`voucher-${voucher.code}`} data-testid="money-row-voucher-code">
              <td style={{ ...LABEL, paddingLeft: 16 }}>
                <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                  {voucher.code}
                </Tag>
              </td>
              <td style={{ ...AMOUNT, color: '#a4193d', fontWeight: 400 }}>
                {(appliedLabels?.vouchers ?? []).length > 1 ? `-${money(voucher.amount)}` : ''}
              </td>
            </tr>
          ))}
          {promotionDiscount > 0 && (
            <tr data-testid="money-row-promotion">
              <td style={LABEL}>Khuyến mãi tự động:</td>
              <td style={{ ...AMOUNT, color: '#389e0d' }}>-{money(promotionDiscount)}</td>
            </tr>
          )}
          {/* TCR-1044: tên chương trình KM tự động đã áp — "khi áp dụng tự động
              phải hiển thị mã chương trình đã áp dụng để đối chiếu". */}
          {(appliedLabels?.promotions ?? []).map((promotion, index) => (
            <tr
              key={`promotion-${promotion.name}-${index}`}
              data-testid="money-row-promotion-name"
            >
              <td style={{ ...LABEL, paddingLeft: 16 }}>
                <Tag color="purple" style={{ marginInlineEnd: 0 }}>
                  {promotion.name}
                </Tag>
              </td>
              <td style={{ ...AMOUNT, color: '#389e0d', fontWeight: 400 }}>
                -{money(promotion.amount)}
              </td>
            </tr>
          ))}
          {tradeDiscount > 0 && (
            <tr>
              <td style={LABEL}>Chiết khấu tích luỹ:</td>
              <td style={{ ...AMOUNT, color: '#a4193d' }}>-{money(tradeDiscount)}</td>
            </tr>
          )}
          {loyaltyRedeemValue > 0 && (
            <tr>
              <td style={LABEL}>Điểm tích luỹ quy đổi:</td>
              <td style={{ ...AMOUNT, color: '#a4193d' }}>-{money(loyaltyRedeemValue)}</td>
            </tr>
          )}

          <tr data-testid="money-row-taxable" data-label="Tiền hàng chưa có VAT">
            <td style={BOLD_LABEL}>Tiền hàng chưa có VAT:</td>
            <td style={BOLD_AMOUNT}>{money(m.goodsTaxableAmount)}</td>
          </tr>

          <tr data-testid="money-row-surcharge" data-label="Phụ thu">
            {readOnly ? (
              <>
                <td style={LABEL}>Phụ thu:</td>
                <td style={AMOUNT}>{money(surchargeLine?.amount ?? 0)}</td>
              </>
            ) : (
              <ServiceFeeRow
                kind="surcharge"
                label="Phụ thu"
                options={serviceOptions?.surcharge ?? []}
                defaultVatRate={defaultVatRates?.surcharge ?? 0}
                value={surchargeRow?.value ?? { serviceId: null, amount: 0 }}
                onChange={(v) => surchargeRow?.onChange(v)}
                optionsLoading={serviceOptionsLoading}
              />
            )}
          </tr>

          {showShippingRow && (
            <tr data-testid="money-row-shipping" data-label="Phí vận chuyển">
              {shippingMode === 'carrier' ? (
                <>
                  <td style={LABEL}>Phí vận chuyển:</td>
                  <td data-testid="carrier-shipping-fee" style={AMOUNT}>
                    {money(carrierFee)}
                  </td>
                </>
              ) : readOnly ? (
                <>
                  <td style={LABEL}>Phí vận chuyển:</td>
                  <td style={AMOUNT}>{money(shippingLine?.amount ?? 0)}</td>
                </>
              ) : (
                <ServiceFeeRow
                  kind="shipping"
                  label="Dịch vụ phí vận chuyển"
                  options={serviceOptions?.shipping ?? []}
                  defaultVatRate={defaultVatRates?.shipping ?? 0}
                  value={shippingRow?.value ?? { serviceId: null, amount: 0 }}
                  onChange={(v) => shippingRow?.onChange(v)}
                  optionsLoading={serviceOptionsLoading}
                />
              )}
            </tr>
          )}
          {/* TCR-2605 — dòng phí vận chuyển ƯỚC TÍNH của hãng, chỉ hiện ở POS
              (cờ `hideCarrierFeeRow`) với đơn đi ĐVVC: dòng phí thật đã bị ẩn
              theo TCR-1084/1086, thu ngân cần biết phải thu hộ hãng bao nhiêu
              và ai chịu khoản đó. Số hiện là ước tính của hãng lúc báo giá —
              KHÔNG phải số đã ghi vào đơn, nên nhãn ghi rõ "(ước tính)".
              Ở màn chi tiết/sửa đơn (không bật cờ) dòng phí thật vẫn hiện. */}
          {showCarrierFeeEstimate && (
            <tr data-testid="carrier-fee-estimate-row" data-label="Phí vận chuyển ước tính">
              <td style={LABEL} title={CARRIER_FEE_ESTIMATE_HINT}>
                Phí vận chuyển ước tính:
                {selectedCarrierFeeStatus === 'ok' && (
                  <div style={{ fontSize: 11, color: '#999', lineHeight: 1.5, paddingTop: 2 }}>
                    {feePayer === 'buyer'
                      ? 'Khách trả (đã cộng vào COD)'
                      : 'Shop trả, không thu của khách'}
                  </div>
                )}
              </td>
              <td style={{ ...AMOUNT, verticalAlign: 'top' }}>
                {selectedCarrierFeeStatus === 'loading' ? (
                  <span style={{ color: '#999', fontWeight: 400 }}>Đang tính…</span>
                ) : (
                  money(carrierFee)
                )}
              </td>
            </tr>
          )}
          {/* Freeship — dòng RIÊNG trong khối vận chuyển, cố ý KHÔNG trộn vào
              chip khuyến mãi tiền hàng: hai trục tiền khác nhau, và thu ngân
              cần thấy phí gốc vẫn còn đó (cửa hàng bỏ ra) bên cạnh phần khách
              được miễn. Số lấy từ pipeline nên đã kẹp về [0, phí khách trả]. */}
          {m.shippingDiscount > 0 && (
            <tr data-testid="pos-shipping-discount" data-label="Miễn phí vận chuyển">
              <td style={LABEL}>Miễn phí vận chuyển:</td>
              <td style={{ ...AMOUNT, color: '#a4193d' }}>-{money(m.shippingDiscount)}</td>
            </tr>
          )}
          {/* Tên chương trình gánh phí ship — cùng vai trò với chip khuyến mãi
              tiền hàng ở trên, chỉ khác trục tiền. */}
          {(appliedLabels?.shippingPromotions ?? []).map((promotion, index) => (
            <tr
              key={`shipping-promotion-${promotion.name}-${index}`}
              data-testid="money-row-shipping-promotion-name"
            >
              <td style={{ ...LABEL, paddingLeft: 16 }}>
                <Tag color="purple" style={{ marginInlineEnd: 0 }}>
                  {promotion.name}
                </Tag>
              </td>
              <td style={{ ...AMOUNT, color: '#a4193d', fontWeight: 400 }}>
                -{money(promotion.amount)}
              </td>
            </tr>
          ))}
          {showShippingRow && shippingMode === 'carrier' && carrierFeeUnavailable && (
            <tr>
              <td colSpan={2} style={{ paddingBottom: 6 }}>
                <div role="alert" style={{ fontSize: 11, color: '#faad14', lineHeight: 1.5 }}>
                  Chưa tính được phí — đơn vẫn ghi được, phí sẽ cập nhật khi đi đơn
                </div>
              </td>
            </tr>
          )}

          <tr data-testid="money-row-vat" data-label="Thuế VAT">
            <td style={LABEL}>
              Thuế VAT:
              {hasTaxDetail && (
                <button
                  type="button"
                  onClick={() => setShowTaxDetail((v) => !v)}
                  style={{
                    marginLeft: 6,
                    border: 'none',
                    background: 'none',
                    color: '#a4193d',
                    cursor: 'pointer',
                    fontSize: 12,
                    padding: 0,
                  }}
                >
                  {showTaxDetail ? '▾' : '▸'} Chi tiết
                </button>
              )}
            </td>
            <td style={AMOUNT}>{money(m.totalVat)}</td>
          </tr>
          {showTaxDetail && hasTaxDetail && (
            <tr>
              <td colSpan={2} style={{ paddingBottom: 6 }}>
                <table style={{ width: '100%', fontSize: 12, color: '#666' }}>
                  <tbody>
                    {m.taxBreakdowns.map((g) => (
                      <tr key={g.vatRate} data-testid={`tax-group-${g.vatRate}`}>
                        <td>VAT {g.vatRate}%</td>
                        <td style={{ textAlign: 'right' }}>{money(g.taxableAmount)}</td>
                        <td style={{ textAlign: 'right' }}>{money(g.vatAmount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </td>
            </tr>
          )}

          <tr data-testid="money-row-total" data-label="Tổng tiền sau VAT">
            <td style={{ color: '#333', fontWeight: 700, fontSize: 15 }}>Tổng tiền sau VAT:</td>
            <td
              data-testid="order-info-total"
              style={{ textAlign: 'right', fontWeight: 700, fontSize: 15 }}
            >
              {money(m.customerPayable)}
            </td>
          </tr>
        </tbody>
      </table>

      {hasShipping && (
        <>
          <div
            data-testid="prepaid-divider"
            style={{ borderTop: '1px dashed #d9d9d9', margin: '10px 0' }}
          />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <tbody>
              <tr>
                <td style={LABEL}>Khách trả trước:</td>
                {!feeFieldsEditable ? (
                  <td style={AMOUNT}>{money(prepaidAmount)}</td>
                ) : (
                  <td>
                    <InputNumber
                      aria-label="Khách trả trước"
                      className="pos-money-input-right"
                      variant="underlined"
                      size="small"
                      controls={false}
                      style={{ width: '100%' }}
                      min={0}
                      // Kẹp trần: trả trước hơn số phải thu là tiền thừa, không phải COD âm.
                      // `collectTotal` gồm cả phí ĐVVC người mua trả — trần đúng
                      // của "khách có thể trả trước tối đa", không phải `customerPayable`.
                      max={m.collectTotal}
                      value={prepaidAmount}
                      placeholder="0"
                      formatter={moneyFormatter}
                      parser={moneyParser}
                      onKeyDown={moneyInputKeyDownGuard}
                      onChange={(v) => onPrepaidChange?.(Math.min(Math.max(0, v ?? 0), m.collectTotal))}
                    />
                  </td>
                )}
              </tr>
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
